import graphql from "babel-plugin-relay/macro";
import { Suspense, useMemo, useState } from "react";
import {
  PreloadedQuery,
  usePreloadedQuery,
  useSubscription,
} from "react-relay";
import { GraphQLSubscriptionConfig } from "relay-runtime";

import AccordionCard from "@whoosh/design-system/src/components/controls/AccordionCard";
import { Loader } from "@whoosh/design-system/src/components/display";
import { Box } from "@whoosh/design-system/src/components/primitive";

import { WaitlistBody } from "./WaitlistBody";
import { WaitlistRendererQuery } from "./__generated__/WaitlistRendererQuery.graphql";
import { WaitlistRendererQueueSlotSubscription } from "./__generated__/WaitlistRendererQueueSlotSubscription.graphql";

type WaitlistRendererProps = {
  alwaysOpen?: boolean;
  queryRef: PreloadedQuery<WaitlistRendererQuery>;
};

export const WaitlistRenderer = ({
  alwaysOpen,
  queryRef,
}: WaitlistRendererProps) => {
  const data = usePreloadedQuery(WaitlistRendererQueryString, queryRef);
  const hasWaitlistRestrictions =
    !!data?.node?.agendas?.edges?.[0]?.node?.waitlistRestrictions?.edges
      ?.length;

  const [waitlistOpen, setWaitlistOpen] = useState(!!alwaysOpen);

  const agenda = data.node?.agendas?.edges
    ? data.node.agendas.edges[0]?.node
    : null;

  const InnerContent = () => {
    return (
      <Box height="100%">
        <Suspense
          fallback={
            <Box pb={6}>
              <Loader />
            </Box>
          }
        >
          {queryRef ? <WaitlistBody fragmentRef={data.node ?? null} /> : null}
        </Suspense>
      </Box>
    );
  };

  const hasBookings =
    agenda?.queueSlot.bookings?.edges &&
    agenda?.queueSlot.bookings?.edges?.length > 0;

  return (
    <>
      {agenda?.id ? <QueueSlotSubscription agendaId={agenda.id} /> : null}
      {!hasBookings && !hasWaitlistRestrictions ? null : (
        <AccordionCard
          Title="Waitlist"
          alwaysOpen={!!alwaysOpen}
          hideIcon={!!alwaysOpen}
          isOpen={waitlistOpen}
          onChange={setWaitlistOpen}
          InnerContent={<InnerContent />}
        />
      )}
    </>
  );
};

const QueueSlotSubscription = ({ agendaId }: { agendaId: string }) => {
  const config: GraphQLSubscriptionConfig<WaitlistRendererQueueSlotSubscription> =
    useMemo(() => {
      return {
        variables: { agendaId },
        subscription: WaitlistRendererQueueSlotSubscriptionString,
      };
    }, [agendaId]);

  useSubscription(config);

  return null;
};

export const WaitlistRendererQueryString = graphql`
  query WaitlistRendererQuery(
    $facilityId: ID!
    $date: Date!
    $courseIds: [ID!]
  ) {
    node(id: $facilityId) {
      ... on Facility {
        ...WaitlistBodyFragment
        agendas(dateGte: $date, dateLte: $date) {
          edges {
            node {
              id
              queueSlot {
                id
                bookings(
                  first: 1000
                  orderBy: { field: REQUESTED_DATE_TIME, direction: ASC }
                ) @connection(key: "queueSlot_bookings") {
                  edges {
                    node {
                      id
                    }
                  }
                }
              }
              waitlistRestrictions: bookingRules(
                type: [WAITLIST]
                courseIds: $courseIds
              ) {
                edges {
                  liftAt
                  node {
                    id
                  }
                }
              }
            }
          }
        }
      }
    }
  }
`;

const WaitlistRendererQueueSlotSubscriptionString = graphql`
  subscription WaitlistRendererQueueSlotSubscription($agendaId: ID!) {
    queueSlotChanged(agendaIds: [$agendaId]) {
      slots {
        ... on QueueSlot {
          id
          queueCount: bookings {
            count
          }
          bookings(
            first: 1000
            orderBy: { field: REQUESTED_DATE_TIME, direction: ASC }
          ) @connection(key: "queueSlot_bookings") {
            edges {
              node {
                id
                ... on StandardBooking {
                  request {
                    time
                  }
                }

                ...WaitlistBooking_Booking
              }
            }
          }
        }
      }
    }
  }
`;
